import { ref } from 'vue';

const scrollContainerRef = ref(null);

export function useScrollHistory() {
  const setScrollContainer = (el) => {
    scrollContainerRef.value = el;
  };

  const scrollToTop = () => {
    if (scrollContainerRef.value) {
      scrollContainerRef.value.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Clear scroll state for fresh navigation
  const clearScrollState = () => {
    const cleanState = { ...history.state };
    delete cleanState.section;
    delete cleanState.scrollY;
    history.replaceState(
      cleanState,
      '',
      window.location.pathname + window.location.search
    );
  };

  const recordSection = (sectionId) => {
    history.replaceState(
      { ...history.state, section: sectionId },
      '',
      window.location.pathname + window.location.search
    );
  };

  const recordScrollY = () => {
    const scrollY = scrollContainerRef.value
      ? scrollContainerRef.value.scrollTop
      : window.scrollY;

    history.replaceState(
      { ...history.state, scrollY },
      '',
      window.location.pathname + window.location.search
    );
  };

  const navigateToSection = (sectionId) => {
    history.pushState(
      { ...history.state, section: sectionId },
      '',
      window.location.pathname + window.location.search
    );
    scrollToElement(sectionId);
  };

  const scrollToElement = (sectionId) => {
    const el = document.getElementById(sectionId);
    if (!el) return;

    if (scrollContainerRef.value) {
      // Calculate position relative to scroll container
      const containerRect = scrollContainerRef.value.getBoundingClientRect();
      const elementRect = el.getBoundingClientRect();
      const scrollTop = scrollContainerRef.value.scrollTop;
      const targetScroll = scrollTop + (elementRect.top - containerRect.top);

      scrollContainerRef.value.scrollTo({
        top: targetScroll,
        behavior: 'smooth',
      });
    } else {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const restoreFromState = (state) => {
    if (!state) return;

    let observer = null;
    let timeoutId = null;
    let retryCount = 0;
    const maxRetries = 30; // 6 seconds
    const retryDelay = 200;
    let hasScrolled = false; // Track if we've successfully scrolled

    const cleanup = () => {
      if (observer) observer.disconnect();
      if (timeoutId) clearTimeout(timeoutId);
    };

    const performScroll = (scrollY) => {
      if (hasScrolled) return; // Don't scroll multiple times

      cleanup();
      hasScrolled = true;
      if (scrollContainerRef.value) {
        scrollContainerRef.value.scrollTo({ top: scrollY, behavior: 'smooth' });
      } else {
        window.scrollTo({ top: scrollY, behavior: 'smooth' });
      }
    };

    const checkAndScroll = () => {
      if (hasScrolled) return true; // Already done

      if (state.section) {
        const el = document.getElementById(state.section);
        if (el) {
          cleanup();
          hasScrolled = true;
          scrollToElement(state.section);
          return true;
        }
      } else if (state.scrollY != null) {
        const target = scrollContainerRef.value || document.documentElement;
        const scrollHeight = scrollContainerRef.value
          ? scrollContainerRef.value.scrollHeight
          : Math.max(
              document.documentElement.scrollHeight,
              document.body.scrollHeight
            );

        // Content is ready if height is sufficient
        if (scrollHeight >= state.scrollY + 100) {
          performScroll(state.scrollY);
          return true;
        }
      }
      return false;
    };

    // Try immediately first
    if (checkAndScroll()) return;

    // Watch for DOM changes
    const targetNode = scrollContainerRef.value || document.body;
    observer = new MutationObserver(() => {
      if (checkAndScroll()) {
        return;
      }
    });

    observer.observe(targetNode, {
      childList: true,
      subtree: true,
    });

    // Polling
    const poll = () => {
      retryCount++;

      if (checkAndScroll()) {
        return;
      }

      if (retryCount < maxRetries) {
        timeoutId = setTimeout(poll, retryDelay);
      } else {
        // TIMEOUT REACHED - but keep MutationObserver running!

        // Stop polling but DON'T disconnect MutationObserver
        // It will continue watching and scroll when content is ready

        // Optional: Set a final hard timeout to cleanup observer after 30 seconds
        setTimeout(() => {
          if (!hasScrolled) {
            cleanup();

            // Last resort: scroll to whatever position is possible
            if (state.scrollY != null) {
              performScroll(state.scrollY);
            }
          }
        }, 30000); // 30 seconds hard limit
      }
    };

    timeoutId = setTimeout(poll, retryDelay);
  };

  let scrollTimer = null;
  const startPassiveScrollTracking = () => {
    const target = scrollContainerRef.value || window;
    const handler = () => {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(recordScrollY, 150);
    };

    if (scrollContainerRef.value) {
      scrollContainerRef.value.addEventListener('scroll', handler, {
        passive: true,
      });
      return () =>
        scrollContainerRef.value?.removeEventListener('scroll', handler);
    } else {
      window.addEventListener('scroll', handler, { passive: true });
      return () => window.removeEventListener('scroll', handler);
    }
  };

  return {
    recordSection,
    recordScrollY,
    navigateToSection,
    restoreFromState,
    startPassiveScrollTracking,
    scrollToTop,
    setScrollContainer,
    clearScrollState,
  };
}
